import { useState, useMemo } from "react";
import { BarChart2 } from "lucide-react";
import { Card } from "@components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";

type ClubStats = {
  club: string;
  totalGames: number;
  totalPrizes: number;
  totalQuestions: number;
  playerCount: number;
  avgPrize: number;
};

type SortableClubKey =
  | keyof Pick<
      ClubStats,
      "club" | "totalGames" | "avgPrize" | "totalQuestions" | "playerCount"
    >
  | "rank";

interface ClubPerformanceProps {
  data: any[];
  loading: boolean;
  error: string | null;
  startDate?: Date;
  endDate?: Date;
}

export function ClubPerformance({
  data,
  loading,
  error,
  startDate,
  endDate,
}: ClubPerformanceProps) {
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<string>("totalGames");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const pageSize = 10;
  // Filter by startDate and endDate using a generic 'date' field if present
  let filtered = Array.isArray(data) ? data : [];
  if (startDate || endDate) {
    filtered = filtered.filter((p: any) => {
      const dateStr = p.date;
      if (!dateStr) return true;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return true;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }
  // Group by clubName and sum gamePlayed and prizeWon
  const clubMap: Record<
    string,
    {
      clubName: string;
      totalGames: number;
      totalPrizes: number;
      playerCount: number;
    }
  > = {};
  filtered.forEach((p: any) => {
    const clubName = p.clubName || "Unknown";
    if (!clubMap[clubName]) {
      clubMap[clubName] = {
        clubName,
        totalGames: 0,
        totalPrizes: 0,
        playerCount: 0,
      };
    }
    clubMap[clubName].totalGames += Number(p.gamePlayed ?? 0);
    clubMap[clubName].totalPrizes += Number(p.prizeWon ?? 0);
    clubMap[clubName].playerCount += 1;
  });
  const clubs = useMemo(() => Object.values(clubMap), [filtered]);
  const sortedClubs = useMemo(() => {
    const arr = [...clubs];
    arr.sort((a, b) => {
      let aVal = a[sortKey as keyof typeof a];
      let bVal = b[sortKey as keyof typeof b];
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
        return 0;
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
    return arr;
  }, [clubs, sortKey, sortDir]);
  const totalPages = Math.ceil(sortedClubs.length / pageSize);
  const pagedClubs = sortedClubs.slice((page - 1) * pageSize, page * pageSize);
  const columns = [
    { key: "rank", label: "Rank" },
    { key: "clubName", label: "Club" },
    { key: "playerCount", label: "Players" },
    { key: "totalGames", label: "Games" },
    { key: "totalPrizes", label: "Prizes" },
  ];

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <BarChart2 className="mr-2 h-5 w-5 text-green-500" />
        Club Performance
      </h2>
      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="relative">
            <Table className="rounded-md overflow-hidden border border-gray-200">
              <TableHeader>
                <TableRow className="bg-[#0F87F2]">
                  <TableHead
                    style={{ width: "5%", cursor: "pointer" }}
                    className="select-none text-white text-base font-medium bg-[#0F87F2]"
                    onClick={() => {
                      setSortKey("rank");
                      setSortDir(
                        sortKey === "rank" && sortDir === "desc"
                          ? "asc"
                          : "desc"
                      );
                    }}
                  >
                    Rank{" "}
                    {sortKey === "rank" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                  </TableHead>
                  <TableHead
                    style={{ width: "30%", cursor: "pointer" }}
                    className="select-none text-white text-base font-medium bg-[#0F87F2]"
                    onClick={() => {
                      setSortKey("clubName");
                      setSortDir(
                        sortKey === "clubName" && sortDir === "desc"
                          ? "asc"
                          : "desc"
                      );
                    }}
                  >
                    Club{" "}
                    {sortKey === "clubName"
                      ? sortDir === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </TableHead>
                  <TableHead
                    style={{
                      width: "10%",
                      textAlign: "right",
                      cursor: "pointer",
                    }}
                    className="select-none text-white text-base font-medium bg-[#0F87F2] text-right justify-end"
                    onClick={() => {
                      setSortKey("playerCount");
                      setSortDir(
                        sortKey === "playerCount" && sortDir === "desc"
                          ? "asc"
                          : "desc"
                      );
                    }}
                  >
                    Players{" "}
                    {sortKey === "playerCount"
                      ? sortDir === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </TableHead>
                  <TableHead
                    style={{
                      width: "10%",
                      textAlign: "right",
                      cursor: "pointer",
                    }}
                    className="select-none text-white text-base font-medium bg-[#0F87F2] text-right justify-end"
                    onClick={() => {
                      setSortKey("totalGames");
                      setSortDir(
                        sortKey === "totalGames" && sortDir === "desc"
                          ? "asc"
                          : "desc"
                      );
                    }}
                  >
                    Games{" "}
                    {sortKey === "totalGames"
                      ? sortDir === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </TableHead>
                  <TableHead
                    style={{
                      width: "10%",
                      textAlign: "right",
                      cursor: "pointer",
                    }}
                    className="select-none text-white text-base font-medium bg-[#0F87F2] text-right justify-end"
                    onClick={() => {
                      setSortKey("totalPrizes");
                      setSortDir(
                        sortKey === "totalPrizes" && sortDir === "desc"
                          ? "asc"
                          : "desc"
                      );
                    }}
                  >
                    Prizes{" "}
                    {sortKey === "totalPrizes"
                      ? sortDir === "asc"
                        ? "▲"
                        : "▼"
                      : ""}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedClubs.map((c, i) => (
                  <TableRow key={c.clubName}>
                    <TableCell style={{ width: "5%" }}>
                      {(page - 1) * pageSize + i + 1}
                    </TableCell>
                    <TableCell style={{ width: "30%" }}>
                      <span className="text-[#0F87F2] underline">
                        {c.clubName}
                      </span>
                    </TableCell>
                    <TableCell style={{ width: "10%", textAlign: "right" }}>
                      {c.playerCount}
                    </TableCell>
                    <TableCell style={{ width: "10%", textAlign: "right" }}>
                      {c.totalGames}
                    </TableCell>
                    <TableCell style={{ width: "10%", textAlign: "right" }}>
                      {c.totalPrizes}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="h-1 bg-[#0F87F2] w-full z-10" />
          </div>
          <div className="flex items-center justify-between mt-4">
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === 1
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${
                page === totalPages || totalPages === 0
                  ? "bg-gray-100 text-gray-400 cursor-default"
                  : "bg-[#0F87F2] text-white hover:bg-[#0C6DC2] cursor-pointer"
              }`}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
